import { formatMoney } from '@/lib/money'
import { type RevisionBaseline } from './DiffBits'
import { type PlanNodeFromServer } from './VersionViewOverallSection'

// What a revision does to the money, for the banner over a diff: the plan's
// total before and after, and the top-level rows that moved the most. A CEO
// reads a revision by its size, not by how many cells it touched.
// A quarterly revision compares full years (sold months + remaining target);
// a full-plan revision compares the plan targets directly.
export interface RevisionMove {
  label: string
  was: number
  now: number
  moves: { name: string; was: number; now: number }[]
}

const TOP_MOVES = 3

export function revisionMove(baseline: RevisionBaseline, planNodes: PlanNodeFromServer[]): RevisionMove {
  const quarterly = baseline.total_full_year !== undefined
  const nowOf = (n: PlanNodeFromServer) =>
    quarterly ? baseline.node_full_year?.[n.id] ?? n.targetRevenue : n.targetRevenue

  const rows = [
    ...planNodes.map((n) => ({
      name: n.shortName?.trim() ? n.shortName : n.name,
      was: baseline.node_targets[n.id] ?? 0,
      now: nowOf(n),
    })),
    ...baseline.removed_nodes.filter((n) => n.depth === 0).map((n) => ({ name: n.name, was: n.target_revenue, now: 0 })),
  ]

  return {
    label: quarterly ? 'Full-year target' : 'Plan target',
    was: baseline.total_target,
    now: quarterly ? baseline.total_full_year! : planNodes.reduce((sum, n) => sum + n.targetRevenue, 0),
    moves: rows
      .filter((r) => r.now !== r.was)
      .sort((a, b) => Math.abs(b.now - b.was) - Math.abs(a.now - a.was))
      .slice(0, TOP_MOVES),
  }
}

function signed(n: number, text: string): string {
  return `${n < 0 ? '−' : '+'}${text}`
}

// The change as signed money and the % of the old value: ["−₫283B", "−32%"].
// No % when the old value is zero.
function changeParts(was: number, now: number, currency: string | null): [string, string | null] {
  const delta = now - was
  const money = signed(delta, formatMoney(Math.abs(delta), currency))
  if (was === 0) return [money, null]
  const pct = Math.abs((delta / was) * 100)
  return [money, signed(delta, `${pct < 10 ? Math.round(pct * 10) / 10 : Math.round(pct)}%`)]
}

// "−₫202B (−27%)"; a row that had nothing before reads "+₫5B (new)".
export function formatChange(was: number, now: number, currency: string | null): string {
  const [money, pct] = changeParts(was, now, currency)
  return `${money} (${pct ?? 'new'})`
}

// "Full-year target cut from ₫879B to ₫596B (−₫283B, −32%)"
export function headline(move: RevisionMove, currency: string | null): string {
  const { label, was, now } = move
  if (was === now) return `${label} unchanged at ${formatMoney(now, currency)}`
  const verb = now < was ? 'cut' : 'raised'
  const change = changeParts(was, now, currency).filter(Boolean).join(', ')
  return `${label} ${verb} from ${formatMoney(was, currency)} to ${formatMoney(now, currency)} (${change})`
}
